import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, CardBody, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { DateOnly } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { formatCurrency } from "utils/currency";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  getClientAdvances,
  receiveClientAdvance,
  refundClientAdvance,
} from "features/ClientAdvances/services/ClientAdvanceService";
import {
  ClientAdvanceSourceType,
  ClientAdvanceStatus,
  clientAdvanceStatusMap,
} from "features/ClientAdvances/constants/client-advance.constants";
import { ClientAdvanceSettlementSourceTypeLabel } from "features/ClientAdvanceSettlements/constants/client-advance-settlement.constants";
import type { ClientAdvance } from "features/ClientAdvances/types/client-advance.types";
import ReceiveModal from "../ReceiveModal";
import RefundModal from "../RefundModal";
import LivePreviewBadge from "./LivePreviewBadge";

type Props = {
  sourceType: ClientAdvanceSourceType;
  sourceId: number;
  className?: string;
};

export default function ClientAdvancesCard({ sourceType, sourceId, className }: Props) {
  const { can } = useAccess();
  const canReadAdvance = can(Permissions.CLIENT_ADVANCE.READ);
  const canReceiveAdvance = can(Permissions.CLIENT_ADVANCE.RECEIVE);
  const canRefundAdvance = can(Permissions.CLIENT_ADVANCE.REFUND);

  const [advances, setAdvances] = useState<ClientAdvance[]>([]);
  const [loading, setLoading] = useState(true);
  const [receiveTarget, setReceiveTarget] = useState<ClientAdvance | null>(null);
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);
  const [refundTarget, setRefundTarget] = useState<ClientAdvance | null>(null);
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return getClientAdvances({ sourceType, sourceId, limit: 10 })
      .then((res) => {
        if (res.success) setAdvances(res.data.rows ?? []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sourceType, sourceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleReceive = async (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => {
    if (!receiveTarget) return;
    setReceiveSubmitting(true);
    try {
      const res = await receiveClientAdvance(receiveTarget.id, payload);
      if (res.success) {
        toast.success(res.message ?? "Advance received.");
        setReceiveTarget(null);
        await load();
      } else {
        toast.error(res.message ?? "Could not receive advance.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Could not receive advance.",
      );
    } finally {
      setReceiveSubmitting(false);
    }
  };

  const handleRefund = async (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => {
    if (!refundTarget) return;
    setRefundSubmitting(true);
    try {
      const res = await refundClientAdvance(refundTarget.id, payload);
      if (res.success) {
        toast.success(res.message ?? "Advance refunded.");
        setRefundTarget(null);
        await load();
      } else {
        toast.error(res.message ?? "Could not refund advance.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Could not refund advance.",
      );
    } finally {
      setRefundSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardBody className="text-center py-3">
          <Spinner size="sm" />
        </CardBody>
      </Card>
    );
  }

  return (
    <React.Fragment>
      <ReceiveModal
        show={!!receiveTarget}
        record={receiveTarget}
        submitting={receiveSubmitting}
        onCloseClick={() => setReceiveTarget(null)}
        onConfirm={(payload) => void handleReceive(payload)}
      />

      <RefundModal
        show={!!refundTarget}
        record={refundTarget}
        submitting={refundSubmitting}
        onCloseClick={() => setRefundTarget(null)}
        onConfirm={(payload) => void handleRefund(payload)}
      />

      <Card className={className}>
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="text-uppercase mb-0">Client Advances</h6>
            <span className="badge bg-light text-dark">{advances.length}</span>
          </div>
          {advances.length === 0 ? (
            <p className="text-muted small mb-0">No advances recorded yet.</p>
          ) : (
            <div className="vstack gap-3">
              {advances.map((adv) => {
                const due = Math.max(0, (adv.amount ?? 0) - (adv.settledAmount ?? 0));
                return (
                  <div key={adv.id} className="border rounded-2 p-2">
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                      <div className="min-w-0">
                        {canReadAdvance ? (
                          <Link
                            to={`/finance/client-advances/${adv.id}`}
                            className="fw-medium text-body text-decoration-none link text-truncate d-block"
                          >
                            {adv.code}
                          </Link>
                        ) : (
                          <span className="fw-medium text-body text-truncate d-block">
                            {adv.code}
                          </span>
                        )}
                      </div>
                      <StatusBadge value={adv.status} map={clientAdvanceStatusMap} />
                    </div>

                    <div className="d-flex flex-wrap justify-content-between gap-2 small mb-1">
                      <span className="text-muted">
                        Amount:{" "}
                        <span className="text-body fw-medium">
                          {formatCurrency(adv.amount)}
                        </span>
                        {adv.isLivePreview ? <LivePreviewBadge /> : null}
                      </span>
                      {(adv.settledAmount ?? 0) > 0 ? (
                        <span className="text-muted">
                          Settled:{" "}
                          <span className="text-success fw-medium">
                            {formatCurrency(adv.settledAmount ?? 0)}
                          </span>
                        </span>
                      ) : null}
                      {due > 0 &&
                      adv.status !== ClientAdvanceStatus.DRAFT &&
                      adv.status !== ClientAdvanceStatus.CANCELLED &&
                      adv.status !== ClientAdvanceStatus.REFUNDED ? (
                        <span className="text-muted">
                          Due: <span className="text-body fw-medium">{formatCurrency(due)}</span>
                        </span>
                      ) : null}
                    </div>

                    {adv.settlements?.length ? (
                      <div className="mt-2 pt-2 border-top border-top-dashed">
                        <div
                          className="text-muted fw-medium mb-1"
                          style={{ fontSize: "0.7rem", letterSpacing: "0.04em" }}
                        >
                          SETTLEMENTS
                        </div>
                        <div className="d-flex flex-column gap-1">
                          {adv.settlements.map((s) => (
                            <div
                              key={s.id}
                              className="d-flex align-items-center justify-content-between gap-2 small"
                            >
                              <span className="text-muted text-truncate">
                                {s.code}
                                {" · "}
                                {ClientAdvanceSettlementSourceTypeLabel[s.sourceType] ??
                                  s.sourceType}
                                {" · "}
                                <DateOnly date={s.created_at} />
                              </span>
                              <span className="text-success fw-medium flex-shrink-0">
                                {formatCurrency(s.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {(canReceiveAdvance && adv.status === ClientAdvanceStatus.DRAFT) ||
                    (canRefundAdvance &&
                      adv.status === ClientAdvanceStatus.PROCESSED &&
                      (adv.settleableAmount ?? 0) > 0) ? (
                      <div className="mt-2 pt-2 border-top border-top-dashed d-flex justify-content-end gap-2">
                        {canReceiveAdvance && adv.status === ClientAdvanceStatus.DRAFT ? (
                          <Button
                            type="button"
                            color="success"
                            size="sm"
                            onClick={() => setReceiveTarget(adv)}
                          >
                            <i className="ri-hand-coin-line align-bottom me-1" />
                            Receive
                          </Button>
                        ) : null}
                        {canRefundAdvance &&
                        adv.status === ClientAdvanceStatus.PROCESSED &&
                        (adv.settleableAmount ?? 0) > 0 ? (
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            onClick={() => setRefundTarget(adv)}
                          >
                            <i className="ri-arrow-go-back-line align-bottom me-1" />
                            Refund
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </React.Fragment>
  );
}
